/**
 * LGPD Block B — consent manager.
 *
 * Single source of truth for the user's privacy/cookie consent state.
 * Preferences live in localStorage under a versioned key; no consent data is
 * sent to the backend in this block (the server-side consent log contract is
 * documented in ``RAG/docs/lgpd_consent_foundation.md`` for the next block).
 *
 * Storage rules:
 *   - Essential is always true and cannot be disabled (auth/session/CSRF).
 *   - Analytics and marketing default to FALSE and only become true through
 *     an explicit user action recorded in ``action``.
 *   - A stored record whose ``policyVersion`` no longer matches the current
 *     privacy policy is treated as absent, so the banner re-prompts after a
 *     policy change.
 */

export const CONSENT_STORAGE_KEY = 'rager_consent_preferences'

/**
 * Must track the published privacy policy version
 * (``public/legal/privacidade.html`` — "Versão: beta-draft-1"). Bump this
 * when the policy materially changes to force a re-prompt.
 */
export const CONSENT_POLICY_VERSION = 'beta-draft-1'

/** Version of the stored JSON shape itself, independent of the policy text. */
export const CONSENT_SCHEMA_VERSION = 1

export type ConsentAction = 'accept_all' | 'reject_all' | 'save_preferences'

export interface ConsentCategories {
  /** Auth, session, security, CSRF — always on, not user-toggleable. */
  essential: true
  /** Future Google Analytics / product analytics. Off until explicit opt-in. */
  analytics: boolean
  /** Future Meta Pixel / marketing scripts. Off until explicit opt-in. */
  marketing: boolean
}

export interface ConsentPreferences {
  schemaVersion: number
  policyVersion: string
  /** ISO-8601 instant of the user's decision. */
  timestamp: string
  categories: ConsentCategories
  /** Which UI action produced this record. */
  action: ConsentAction
}

const CONSENT_CHANGE_EVENT = 'rager:consent-change'
const OPEN_PREFERENCES_EVENT = 'rager:consent-open-preferences'

const VALID_ACTIONS: readonly ConsentAction[] = [
  'accept_all',
  'reject_all',
  'save_preferences',
]

/**
 * Read the stored consent decision. Returns ``null`` when nothing valid is
 * stored — missing, malformed JSON, wrong shape, or a stale policy version —
 * and callers must treat ``null`` as "no consent given" (banner shows,
 * analytics/marketing stay off).
 */
export function getConsent(): ConsentPreferences | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<ConsentPreferences>
    if (
      parsed.schemaVersion !== CONSENT_SCHEMA_VERSION ||
      parsed.policyVersion !== CONSENT_POLICY_VERSION ||
      typeof parsed.timestamp !== 'string' ||
      !parsed.action ||
      !VALID_ACTIONS.includes(parsed.action) ||
      typeof parsed.categories !== 'object' ||
      parsed.categories === null ||
      typeof parsed.categories.analytics !== 'boolean' ||
      typeof parsed.categories.marketing !== 'boolean'
    ) {
      return null
    }
    return {
      schemaVersion: CONSENT_SCHEMA_VERSION,
      policyVersion: parsed.policyVersion,
      timestamp: parsed.timestamp,
      categories: {
        essential: true,
        analytics: parsed.categories.analytics,
        marketing: parsed.categories.marketing,
      },
      action: parsed.action,
    }
  } catch {
    return null
  }
}

/**
 * Persist a consent decision and notify subscribers. ``essential`` is forced
 * to true regardless of input.
 */
export function setConsent(
  categories: { analytics: boolean; marketing: boolean },
  action: ConsentAction,
): ConsentPreferences {
  const record: ConsentPreferences = {
    schemaVersion: CONSENT_SCHEMA_VERSION,
    policyVersion: CONSENT_POLICY_VERSION,
    timestamp: new Date().toISOString(),
    categories: {
      essential: true,
      analytics: categories.analytics === true,
      marketing: categories.marketing === true,
    },
    action,
  }
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record))
    } catch {
      // Quota/privacy-mode failure: the decision still applies in-memory for
      // this page via the change event; the banner will re-prompt next visit.
    }
    window.dispatchEvent(
      new CustomEvent<ConsentPreferences>(CONSENT_CHANGE_EVENT, { detail: record }),
    )
  }
  return record
}

/** True only after an explicit, currently-valid analytics opt-in. */
export function hasAnalyticsConsent(): boolean {
  return getConsent()?.categories.analytics === true
}

/** True only after an explicit, currently-valid marketing opt-in. */
export function hasMarketingConsent(): boolean {
  return getConsent()?.categories.marketing === true
}

/** Remove the stored decision entirely (the banner will show again). */
export function clearConsent(): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(CONSENT_STORAGE_KEY)
  } catch {
    // ignore storage failures
  }
  window.dispatchEvent(
    new CustomEvent<ConsentPreferences | null>(CONSENT_CHANGE_EVENT, { detail: null }),
  )
}

/**
 * Subscribe to consent changes in this tab. The callback receives the new
 * record, or ``null`` after ``clearConsent``. Returns an unsubscribe function.
 */
export function onConsentChange(
  callback: (consent: ConsentPreferences | null) => void,
): () => void {
  if (typeof window === 'undefined') return () => {}
  const handler = (event: Event) => {
    callback((event as CustomEvent<ConsentPreferences | null>).detail ?? null)
  }
  window.addEventListener(CONSENT_CHANGE_EVENT, handler)
  return () => window.removeEventListener(CONSENT_CHANGE_EVENT, handler)
}

/**
 * Ask the consent UI to open the preferences dialog. Event-based (instead of
 * React context) so pre-auth surfaces like the login footer — which render
 * outside the app's state providers — can trigger it with no new plumbing.
 */
export function openConsentPreferences(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(OPEN_PREFERENCES_EVENT))
}

/** Used by the consent UI to react to ``openConsentPreferences`` requests. */
export function onOpenPreferencesRequest(callback: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  const handler = () => callback()
  window.addEventListener(OPEN_PREFERENCES_EVENT, handler)
  return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, handler)
}
